import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "@/app/App";
import { createAppContainer } from "@/app/container";
import { createDexieStore } from "@/data/dexie/DexieStore";
import { browserEnvironment } from "@/services/AnalyticsService";
import { HttpApi } from "@/services/ApiClient";
import { StreamCompressor } from "@/services/ArchiveService";
import { SystemClock } from "@/domain/clock";
import { resolveApiBase, isNativeBuild } from "@/app/apiBase";
import { registerSW } from "virtual:pwa-register";

// Installed PWAs can stay open for days; check for a new build regularly and
// whenever the app returns to the foreground (autoUpdate then reloads). The
// native build serves its bundle locally, so it registers no service worker.
if (!isNativeBuild()) {
  registerSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => registration.update().catch(() => undefined);
      setInterval(check, 15 * 60 * 1000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    },
  });
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

const clock = new SystemClock();
const container = createAppContainer(createDexieStore(clock), clock, new HttpApi(resolveApiBase()), new StreamCompressor(), browserEnvironment());
container.archive.start();
container.reminders.start();
container.systemLanguage.start(container.store.settings);
void container.analytics.sessionStart();
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    void container.archive.run();
    void container.analytics.sessionStart();
  } else {
    void container.analytics.flush(true);
  }
});
window.addEventListener("pagehide", () => void container.analytics.flush(true));

createRoot(root).render(
  <StrictMode>
    <App container={container} />
  </StrictMode>,
);
